import styled from 'styled-components';
import { useMemo } from 'react';

import { c080820, mainGridGapColor } from '#/colors';

import BaseTradeOverview, {
  TabsContent,
  TabsList,
  TabsTrigger,
} from '#/components/shared/trade-overview/TradeOverview';
import { StyledTable } from '#/components/shared/trade-overview/table';
import { TabData } from '#/components/common/tabs';
import Collateral from '#/components/shared/trade-overview/Collateral';
import FundingPayments from '#/components/shared/trade-overview/FundingPayments';
import OpenOrders from '#/components/shared/trade-overview/OpenOrders';
import OpenPositions from '#/components/shared/trade-overview/OpenPositions';
import TabLabelCollateral from '#/components/shared/trade-overview/TabLabelCollateral';
import TabLabelFundingPayments from '#/components/shared/trade-overview/TabLabelFundingPayments';
import TabLabelOpenOrders from '#/components/shared/trade-overview/TabLabelOpenOrders';
import TabLabelOpenPositions from '#/components/shared/trade-overview/TabLabelOpenPositions';
import TabLabelTradeHistory from '#/components/shared/trade-overview/TabLabelTradeHistory';
import TabLabelTransfers from '#/components/shared/trade-overview/TabLabelTransfers';
import TabLabelOrderHistory from '#/components/shared/trade-overview/TabLabelOrderHistory';
import TradeHistory from '#/components/shared/trade-overview/TradeHistory';
import Transfers from '#/components/shared/trade-overview/Transfers';
import OrderHistory from '#/components/shared/trade-overview/OrderHistory';

import {
  PortfolioSearchParams,
  usePortfolioSearchParams,
  usePortfolioSearchParamsActions,
} from '#/features/navigation/portfolio-context';
import { useTranslation } from '#/features/localization/utils';
import { MEDIA } from '#/features/layout/media-queries';

const useTabData = (): readonly TabData<
  PortfolioSearchParams['active_tab']
>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        id: 'collateral',
        label: <TabLabelCollateral />,
        content: (
          <Collateral
            tableSpec={{
              asset: { header: t('Asset'), width: '140px' },
              walletBalance: {
                header: t('Wallet Balance'),
              },
              actions: { header: t('Actions'), width: '168px' },
            }}
          />
        ),
      },
      {
        id: 'positions',
        label: <TabLabelOpenPositions />,
        content: (
          <OpenPositions
            tableSpec={{
              market: { header: t('Market') },
              side: { header: t('Side'), width: '80px' },
              size: { header: t('Size') },
              notional: { header: t('Notional') },
              entryPrice: { header: t('Entry Price') },
              markPrice: { header: t('Mark Price') },
              unrealizedPnl: { header: t('Unrealized P&L') },
              fundingPnl: { header: t('Unrealized Funding'), width: '150px' },
            }}
          />
        ),
      },
      {
        id: 'open-orders',
        label: <TabLabelOpenOrders />,
        content: (
          <OpenOrders
            tableSpec={{
              time: { header: t('Time') },
              market: { header: t('Market') },
              side: { header: t('Side') },
              type: { header: t('Type') },
              price: { header: t('Price') },
              amount: { header: t('Amount') },
              filled: { header: t('Filled') },
            }}
          />
        ),
      },
      {
        id: 'trade-history',
        label: <TabLabelTradeHistory />,
        content: (
          <TradeHistory
            tableSpec={{
              status: { header: t('Status') },
              time: { header: t('Time') },
              market: { header: t('Market') },
              side: { header: t('Side') },
              price: { header: t('Price') },
              amount: { header: t('Amount') },
              total: { header: t('Notional / Fee') },
              liquidity: { header: t('Liquidity') },
              txHash: { header: t('Tx Hash') },
              id: { header: t('Id') },
            }}
          />
        ),
      },
      {
        id: 'order-history',
        label: <TabLabelOrderHistory />,
        content: (
          <OrderHistory
            tableSpec={{
              time: { header: t('Time') },
              market: { header: t('Market') },
              type: { header: t('Type') },
              side: { header: t('Side') },
              price: { header: t('Price') },
              amount: { header: t('Amount') },
              remainingSize: { header: t('Remaining') },
              status: { header: t('Status') },
              orderId: { header: t('Order Id') },
            }}
          />
        ),
      },
      {
        id: 'funding-payments',
        label: <TabLabelFundingPayments />,
        content: (
          <FundingPayments
            tableSpec={{
              time: { header: t('Time'), width: '140px' },
              market: { header: t('Market') },
              payment: {
                header: t('Payment'),
                width: '200px',
                textAlign: 'right',
              },
            }}
          />
        ),
      },
      {
        id: 'deposit-withdraw',
        label: <TabLabelTransfers />,
        content: (
          <Transfers
            tableSpec={{
              time: { header: t('Time'), width: '140px' },
              kind: { header: t('Type') },
              status: { header: t('Status') },
              amount: { header: t('Amount') },
              txHashL2: { header: t('Tx Hash L2') },
              txHashL1: { header: t('Tx Hash L1') },
              action: { header: t('Action'), width: '175px' },
            }}
          />
        ),
      },
    ],
    [t],
  );
};

export default function PortfolioTradeOverview() {
  const tabData = useTabData();
  const portfolioSearchParams = usePortfolioSearchParams();
  const portfolioSearchParamsActions = usePortfolioSearchParamsActions();

  return (
    <TradeOverview
      tabData={tabData}
      activeTab={portfolioSearchParams.active_tab}
      onTabChange={portfolioSearchParamsActions.updateActiveTab}
    />
  );
}

const TradeOverview = styled(BaseTradeOverview)`
  --TABS_LIST_HEIGHT: 36px;
  overflow: unset;

  height: 400px;
  width: 1000px;

  @media ${MEDIA.md} {
    width: unset;
    height: 100%;
  }

  ${TabsList} {
    border-bottom: unset;
    padding: unset;
  }

  ${TabsContent} {
    overflow: unset;
    min-height: 140px;
  }

  ${TabsTrigger} {
    padding: 0 2px;
  }

  ${StyledTable} {
    margin-top: 24px;
    width: 100%;
    table-layout: fixed;

    th,
    td {
      border-bottom: none;
      padding: 1px 16px;
    }

    th:last-child,
    td:last-child {
      width: unset;
    }

    tbody > tr:not(:last-child) {
      border-bottom: 1px solid ${mainGridGapColor};
    }

    thead > tr {
      height: 26px;
    }

    thead > tr > th {
      background: ${c080820};
    }

    tbody {
      box-shadow: inset 0 0 0 1px ${mainGridGapColor};
      border-radius: 7px;
      overflow: auto;
    }
  }
`;
